import { useBindingContext } from "BindingContextHook";
import bindingEvaluator from "BindingEvaluator";
import captionService, { type Caption, type ResourceInfo } from "CaptionService";

interface KeyedCaption {
  key?: string;
  text?: string;
}

type OverrideCaption = KeyedCaption & ResourceInfo;

export function useCaption(
  caption?: Caption | OverrideCaption | string,
  captionOverride?: Caption | OverrideCaption | string,
  bindingPath?: string,
  skipCaptionFallback?: boolean,
): Caption | string {
  if (caption && !captionService.isEmptyCaption(caption)) {
    return getCaption(caption);
  }

  if (captionOverride && !captionService.isEmptyCaption(captionOverride)) {
    return getCaption(captionOverride);
  }

  return getFallbackCaption(bindingPath, skipCaptionFallback);
}

export function isCaption(object: Caption | OverrideCaption | string): object is Caption {
  return typeof object === "object" && object != null && "caption" in object;
}

function getCaption(caption: Caption | OverrideCaption | string): Caption | string {
  if (typeof caption === "string" || isCaption(caption)) {
    return caption;
  }

  const resKey = caption.key || caption.ResKey;
  const fallback = caption.text || caption.Fallback;
  return captionService.getCaptionFromKey(resKey, fallback);
}

function getFallbackCaption(bindingPath?: string, skipCaptionFallback?: boolean): Caption | string {
  let caption: Caption | undefined;
  if (!skipCaptionFallback) {
    if (bindingPath) {
      const bindingContext = useBindingContext();
      const entityType = bindingContext && bindingContext.value && bindingContext.value.entityType;
      if (entityType) {
        const bindingInformation = bindingEvaluator.getEntityBindingInfo(entityType, bindingPath);
        caption = captionService.getCaptionFromField(bindingInformation);
      }
    }
  }

  return getCaption(caption || "");
}
